import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Minus, X, GripHorizontal, Maximize2, Minimize2 } from 'lucide-react';
import { useAppendix } from '../../context/AppendixContext';

const AppendixWrapper = ({ id, title, icon: IconOrElement, children, className = '', defaultPosition, onClose, actions, visible }) => {
    const { appendices, toggleMinimize, toggleMaximize, closeAppendix, updatePosition, bringToFront, getZIndex } = useAppendix();
    const state = appendices[id];
    
    // Use visible prop as fallback if state is not ready yet
    const isVisible = state ? (state.isOpen && !state.isMinimized) : visible;
    const isMaximized = state?.isMaximized || false;
    
    // Local state for immediate drag feedback
    const [localPos, setLocalPos] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    
    const dragInfo = useRef({
        startX: 0,
        startY: 0,
        startWindowX: 0,
        startWindowY: 0
    });

    const wrapperRef = useRef(null);

    // Handler para traer al frente cuando se interactúa con el apéndice
    const handleInteraction = useCallback(() => {
        bringToFront(id);
    }, [id, bringToFront]);

    // Sync local state with global state when not dragging
    useEffect(() => {
        if (!isDragging && state?.position) {
            setLocalPos(state.position);
        } else if (!state?.position && defaultPosition && !localPos) {
            setLocalPos(defaultPosition);
            updatePosition(id, defaultPosition);
        }
    }, [state?.position, id, defaultPosition, updatePosition, isDragging, localPos]);

    const onMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        
        // No permitir arrastrar si está maximizado
        if (isMaximized) return;
        
        // Traer al frente cuando empieza el drag
        bringToFront(id);
        setIsDragging(true);
        
        const currentPos = localPos || { x: 0, y: 0 };
        dragInfo.current = {
            startX: e.pageX,
            startY: e.pageY,
            startWindowX: currentPos.x,
            startWindowY: currentPos.y
        };
        
        e.stopPropagation();
        e.preventDefault();
    }, [localPos, id, bringToFront, isMaximized]);

    const onMouseMove = useCallback((e) => {
        if (!isDragging) return;

        const deltaX = e.pageX - dragInfo.current.startX;
        const deltaY = e.pageY - dragInfo.current.startY;

        const nextX = dragInfo.current.startWindowX + deltaX;
        const nextY = dragInfo.current.startWindowY + deltaY;

        // Bounding logic
        const winW = window.innerWidth;
        const winH = window.innerHeight;
        const elW = wrapperRef.current?.offsetWidth || 0;
        const elH = wrapperRef.current?.offsetHeight || 0;

        const boundedX = Math.max(0, Math.min(nextX, winW - elW));
        const boundedY = Math.max(80, Math.min(nextY, winH - elH));

        setLocalPos({ x: boundedX, y: boundedY });
    }, [isDragging]);

    const onMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            updatePosition(id, localPos);
        }
    }, [isDragging, id, localPos, updatePosition]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove, { passive: false });
            window.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        } else {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, onMouseMove, onMouseUp]);

    if (!isVisible) return null;

    const currentDisplayPos = localPos || { x: 100, y: 100 };
    const SIDEBAR_WIDTH = 291; // Ancho del sidebar de WhatsApp

    // PERFORMANCE OPTIMIZATION: Use translate3d instead of top/left
    // Cuando está maximizado, ocupar el espacio disponible respetando el sidebar
    const style = isMaximized ? {
        position: 'fixed',
        left: `${SIDEBAR_WIDTH}px`,
        top: '80px',
        right: 0,
        bottom: 0,
        transform: 'none',
        width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
        height: 'calc(100vh - 80px)',
        zIndex: getZIndex(id, isDragging)
    } : {
        position: 'fixed',
        left: 0,
        top: 0,
        transform: `translate3d(${currentDisplayPos.x}px, ${currentDisplayPos.y}px, 0)`,
        willChange: 'transform',
        zIndex: getZIndex(id, isDragging)
    };

    const renderIcon = () => {
        if (!IconOrElement) return null;
        if (React.isValidElement(IconOrElement)) {
            return IconOrElement;
        }
        const Icon = IconOrElement;
        return <Icon size={18} className="text-primary" />;
    };

    return (
        <div 
            ref={wrapperRef}
            style={style}
            onMouseDown={handleInteraction}
            className={`flex flex-col bg-slate-950/90 border border-slate-800 ${isMaximized ? 'rounded-none' : 'rounded-3xl'} shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden backdrop-blur-xl pointer-events-auto ${className} ${isDragging ? 'transition-none select-none' : 'transition-all duration-300 ease-out'}`}
        >
            {/* Window Header / Drag Bar */}
            <div 
                onMouseDown={onMouseDown}
                className={`px-6 py-4 bg-slate-900/50 border-b border-slate-800 flex justify-between items-center select-none ${isMaximized ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'} group h-14`}
            >
                <div className="flex items-center gap-3">
                    {!isMaximized && <GripHorizontal size={14} className="text-slate-600 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    {IconOrElement && (
                        <div className="p-2 bg-primary/10 rounded-lg flex items-center justify-center">
                            {renderIcon()}
                        </div>
                    )}
                    <div>
                        <h3 className="font-bold text-slate-100 text-sm tracking-tight">{title}</h3>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {actions && (
                        <div className="mr-2 flex items-center" onMouseDown={e => e.stopPropagation()}>
                            {actions}
                        </div>
                    )}
                    <button 
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => toggleMaximize(id)}
                        className="p-2 hover:bg-slate-800 rounded-full transition-all text-slate-500 hover:text-white"
                        title={isMaximized ? "Restaurar" : "Maximizar"}
                    >
                        {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>
                    <button 
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => toggleMinimize(id)}
                        className="p-2 hover:bg-slate-800 rounded-full transition-all text-slate-500 hover:text-white"
                        title="Minimizar"
                    >
                        <Minus size={16} />
                    </button>
                    <button 
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => {
                            closeAppendix(id);
                            if (onClose) onClose();
                        }}
                        className="p-2 hover:bg-slate-800 rounded-full transition-all text-slate-500 hover:text-red-400"
                        title="Cerrar"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className={`flex-1 overflow-hidden relative ${isDragging ? 'pointer-events-none' : ''}`}>
                {children}
            </div>

            {/* Dragging Overlay to prevent interaction with iframe or text selection during drag */}
            {isDragging && <div className="absolute inset-0 z-[101] cursor-grabbing" />}
        </div>
    );
};

export default AppendixWrapper;
